import { NeonColors } from '../neon-theme'
import { mixColors, toCssHex, type SnakeColors } from '../../game/snakeColors'

const RESULT_STAR_SCALE = 100
const RESULT_TRASH_EMOJI = '🗑️'
const RESULT_STAR_EMOJI = '⭐'

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function drawStarPolygon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  outerRadius: number,
  innerRadius: number,
): void {
  ctx.beginPath()
  for (let i = 0; i < 10; i++) {
    const angle = -Math.PI / 2 + (i * Math.PI) / 5
    const radius = i % 2 === 0 ? outerRadius : innerRadius
    const px = x + Math.cos(angle) * radius
    const py = y + Math.sin(angle) * radius
    if (i === 0) ctx.moveTo(px, py)
    else ctx.lineTo(px, py)
  }
  ctx.closePath()
}

export function formatSparkStarLine(
  sparkCount: number,
  singularLabel: string,
  pluralLabel: string,
): string {
  const stars = Math.floor(sparkCount / RESULT_STAR_SCALE)
  const starText = String(stars)
  const label = sparkCount === 1 ? singularLabel : pluralLabel
  return `${sparkCount} ${label} (${starText} star${stars === 1 ? '' : 's'})`
}

export function getResultSnakeVisualSegments(length: number, canvasWidth: number): number {
  return clamp(Math.round(length * 0.6), 18, Math.max(26, Math.min(84, Math.floor(canvasWidth / 4.2))))
}

export function getResultSnakeSpacing(canvasWidth: number, visualSegments: number): number {
  return clamp(canvasWidth / Math.max(visualSegments + 10, 32), 7.5, 13.5)
}

export interface ResultSnakeFrameOptions {
  canvasWidth: number
  canvasHeight: number
  headX: number
  centerY: number
  length: number
  timeMs: number
  colors: SnakeColors
  fadeToWhite?: number
  alpha?: number
  consumedSegments?: number
}

export function drawResultSnake(
  ctx: CanvasRenderingContext2D,
  options: ResultSnakeFrameOptions,
): void {
  const {
    canvasWidth,
    headX,
    centerY,
    length,
    timeMs,
    colors,
    fadeToWhite = 0,
    alpha = 1,
    consumedSegments = 0,
  } = options

  const visualSegments = getResultSnakeVisualSegments(length, canvasWidth)
  const hiddenSegments = clamp(consumedSegments, 0, visualSegments)
  const spacing = getResultSnakeSpacing(canvasWidth, visualSegments)
  const headRadius = clamp(6 + Math.log2(Math.max(length, 10)) * 1.15, 8, 13.5)
  const tailRadius = Math.max(2.8, headRadius * 0.32)
  const wavePhase = timeMs * 0.0055
  const bodyA = mixColors(colors.body[0], 0xffffff, clamp(fadeToWhite, 0, 1))
  const bodyB = mixColors(colors.body[1], 0xffffff, clamp(fadeToWhite, 0, 1))
  const headColor = mixColors(colors.head, 0xffffff, clamp(fadeToWhite * 0.92, 0, 1))
  const glowColor = toCssHex(mixColors(colors.head, 0xffffff, clamp(fadeToWhite * 0.85, 0, 1)))
  const bodyCss = [toCssHex(bodyA), toCssHex(bodyB)]

  for (let i = visualSegments - 1; i >= Math.max(1, hiddenSegments); i--) {
    const segX = headX - i * spacing
    if (segX < -headRadius * 2) continue
    const t = 1 - i / Math.max(1, visualSegments - 1)
    const radius = tailRadius + (headRadius * 0.74 - tailRadius) * Math.pow(t, 0.75)
    const wave = Math.sin(wavePhase - i * 0.6) * (2.4 + headRadius * 0.22) * (0.55 + t * 0.55)
    const segY = centerY + wave

    ctx.globalAlpha = alpha * (0.22 + t * 0.72)
    ctx.shadowColor = bodyCss[i % 2]
    ctx.shadowBlur = 8 + t * 10
    ctx.beginPath()
    ctx.arc(segX, segY, radius, 0, Math.PI * 2)
    ctx.fillStyle = bodyCss[i % 2]
    ctx.fill()
  }

  if (hiddenSegments === 0) {
    const headWave = Math.sin(wavePhase) * (2.4 + headRadius * 0.26)
    const headY = centerY + headWave
    ctx.globalAlpha = alpha
    ctx.shadowColor = glowColor
    ctx.shadowBlur = 18
    ctx.beginPath()
    ctx.arc(headX, headY, headRadius + 4, 0, Math.PI * 2)
    ctx.fillStyle = `${glowColor}55`
    ctx.fill()

    ctx.shadowBlur = 10
    ctx.beginPath()
    ctx.arc(headX, headY, headRadius, 0, Math.PI * 2)
    ctx.fillStyle = toCssHex(headColor)
    ctx.fill()

    const eyeOffsetY = headRadius * 0.38
    const eyeOffsetX = headRadius * 0.36
    const eyeRadius = Math.max(1.9, headRadius * 0.24)
    const pupilRadius = eyeRadius * 0.5
    for (const sign of [-1, 1]) {
      const ex = headX + eyeOffsetX
      const ey = headY + sign * eyeOffsetY
      ctx.shadowBlur = 0
      ctx.beginPath()
      ctx.arc(ex, ey, eyeRadius, 0, Math.PI * 2)
      ctx.fillStyle = '#ffffff'
      ctx.fill()
      ctx.beginPath()
      ctx.arc(ex + pupilRadius * 0.45, ey, pupilRadius, 0, Math.PI * 2)
      ctx.fillStyle = '#0b0b12'
      ctx.fill()
    }
  }

  ctx.shadowBlur = 0
  ctx.globalAlpha = 1
}

function drawGoalGlow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  color: string,
  intensity: number,
): void {
  ctx.save()
  const gradient = ctx.createRadialGradient(x, y, radius * 0.15, x, y, radius)
  gradient.addColorStop(0, color)
  gradient.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.globalAlpha = clamp(0.22 + intensity * 0.55, 0, 0.9)
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

function drawGoalEmoji(
  ctx: CanvasRenderingContext2D,
  emoji: string,
  x: number,
  y: number,
  size: number,
  pulse: number,
  glowColor: string,
): void {
  const scale = 1 + pulse * 0.12
  drawGoalGlow(ctx, x, y, size * (0.72 + pulse * 0.18), glowColor, pulse)
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(scale, scale)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `${Math.round(size)}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`
  ctx.shadowColor = glowColor
  ctx.shadowBlur = 12 + pulse * 24
  ctx.fillText(emoji, 0, 0)
  ctx.restore()
}

export function drawTrashGoal(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  intensity: number,
): void {
  drawGoalEmoji(ctx, RESULT_TRASH_EMOJI, x, y, size, intensity, 'rgba(255,255,255,0.55)')
}

export function drawStarGoal(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  intensity: number,
): void {
  drawGoalEmoji(ctx, RESULT_STAR_EMOJI, x, y, size, intensity, NeonColors.goldGlow)
  const pulse = 1 + intensity * 0.16
  const outer = size * 0.24 * pulse
  const inner = outer * 0.48
  ctx.save()
  ctx.globalAlpha = 0.9
  ctx.shadowColor = NeonColors.goldGlow
  ctx.shadowBlur = 10 + intensity * 16
  drawStarPolygon(ctx, x, y, outer, inner)
  ctx.fillStyle = 'rgba(255,255,255,0.9)'
  ctx.fill()
  ctx.restore()
}

export function drawStarBurst(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  intensity: number,
  timeMs: number,
): void {
  if (intensity <= 0) return
  const spokes = 7
  const baseRadius = 14 + intensity * 10
  const alpha = clamp(1 - timeMs / 420, 0, 1)
  if (alpha <= 0) return

  ctx.save()
  ctx.globalAlpha = alpha
  ctx.strokeStyle = NeonColors.gold
  ctx.fillStyle = '#ffffff'
  ctx.shadowColor = NeonColors.goldGlow
  ctx.shadowBlur = 16 + intensity * 12
  for (let i = 0; i < spokes; i++) {
    const angle = (i / spokes) * Math.PI * 2 + timeMs * 0.01
    const inner = baseRadius * 0.2
    const outer = baseRadius * (0.8 + i * 0.05)
    ctx.beginPath()
    ctx.moveTo(x + Math.cos(angle) * inner, y + Math.sin(angle) * inner)
    ctx.lineTo(x + Math.cos(angle) * outer, y + Math.sin(angle) * outer)
    ctx.lineWidth = 2 + intensity * 0.5
    ctx.stroke()
    ctx.beginPath()
    ctx.arc(
      x + Math.cos(angle) * outer,
      y + Math.sin(angle) * outer,
      1.8 + intensity * 0.45,
      0,
      Math.PI * 2,
    )
    ctx.fill()
  }
  ctx.restore()
}
